import React from "react";
import { Typography } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, EffectFade, Pagination } from "swiper/modules";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

import hero1 from "../../images/slider/slide-test1.png";
import hero2 from "../../images/slider/slide-test2.png";
import hero3 from "../../images/slider/slide-test3.png";
import hero4 from "../../images/slider/slide-test4.png";

const slides = [
  {
    image: hero1,
    title: "Expertise with a Personal Touch",
    text: "Our size allows us to deliver deep domain expertise without compromising on personalized attention. Every client receives tailored solutions from qualified professionals.",
    btnText: "Learn More",
    btnLink: "/about",
  },
  {
    image: hero2,
    title: "Pan-India & Global Reach",
    text: "While rooted in India, our services extend globally. We assist clients with cross-border taxation, transfer pricing, and FEMA compliance.",
    btnText: "Our Presence",
    btnLink: "/contact",
  },
  {
    image: hero3,
    title: "Comprehensive Portfolio",
    text: "We offer end-to-end financial, taxation, audit, and advisory services under one roof — helping you navigate today’s dynamic regulatory environment.",
    btnText: "Explore Services",
    btnLink: "/service-group/advisory-deals",
  },
  {
    image: hero4,
    title: "Your Success, Our Mission",
    text: "At the heart of our firm is a simple yet powerful belief — your success defines ours. We go beyond compliance to become your long-term strategic partner.",
    btnText: "Contact Us",
    btnLink: "/contact",
  },
];

const Hero = () => {
  return (
    <section className="wpo-hero-slider">
      <Swiper
        modules={[Navigation, Autoplay, EffectFade, Pagination]}
        effect="fade"
        loop={true}
        speed={1500}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        navigation={true}
        pagination={{ clickable: true }}
        className="hero-slider"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>
            <div
              className="slide-inner"
              style={{
                backgroundImage: `url(${slide.image})`,
                height: "90vh",
                backgroundSize: "cover",
                backgroundPosition: "center",
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              {/* Overlay */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, rgba(26, 66, 161, 0.7) 0%, rgba(0,0,0,0.3) 100%)",
                }}
              />

              <div
                className="container hero-container"
                style={{
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <div className="row">
                  <div className="col-lg-8">
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      <Typography
                        variant="h1"
                        sx={{
                          color: "#fff",
                          fontWeight: 800,
                          fontSize: { xs: "2rem", md: "4rem" },
                          mb: 2,
                          fontFamily: "'Roboto Slab', serif",
                          lineHeight: 1.2,
                        }}
                      >
                        {slide.title}
                      </Typography>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          color: "rgba(255,255,255,0.9)",
                          mb: 5,
                          maxWidth: "600px",
                          fontWeight: 400,
                          lineHeight: 1.6,
                          fontSize: { xs: "0.95rem", md: "1.25rem" },
                        }}
                      >
                        {slide.text}
                      </Typography>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                      className="slide-btns"
                    >
                      <Link
                        to={slide.btnLink}
                        className="theme-btn"
                        style={{
                          background: "var(--primary-color)",
                          color: "#fff",
                          padding: "10px 32px",
                          borderRadius: "50px",
                          textDecoration: "none",
                          fontWeight: 700,
                          display: "inline-block",
                          boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                          transition: "all 0.3s",
                        }}
                      >
                        {slide.btnText}
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <style>{`
        .wpo-hero-slider .swiper {
          position: relative;
        }
        .wpo-hero-slider .swiper-button-next, 
        .wpo-hero-slider .swiper-button-prev {
          color: #fff;
          background: transparent !important;
          width: 40px;
          height: 40px;
          border-radius: 0;
          border: none !important;
          backdrop-filter: none !important;
          transition: all 0.3s;
          z-index: 10;
        }
        .wpo-hero-slider .swiper-button-prev {
          left: 10px;
        }
        .wpo-hero-slider .swiper-button-next {
          right: 10px;
        }
        /* Completely hide themify :before icons */
        .wpo-hero-slider .swiper-button-next::before, 
        .wpo-hero-slider .swiper-button-prev::before {
          content: none !important;
          display: none !important;
        }
        /* Use Swiper's :after with unicode arrows */
        .wpo-hero-slider .swiper-button-prev::after {
          content: '‹' !important;
          font-family: Arial, sans-serif !important;
          font-size: 60px !important;
          font-weight: 200 !important;
          color: #fff !important;
          opacity: 0.7;
        }
        .wpo-hero-slider .swiper-button-next::after {
          content: '›' !important;
          font-family: Arial, sans-serif !important;
          font-size: 60px !important;
          font-weight: 200 !important;
          color: #fff !important;
          opacity: 0.7;
        }
        .wpo-hero-slider .swiper-button-next:hover::after, 
        .wpo-hero-slider .swiper-button-prev:hover::after {
          opacity: 1;
          transform: scale(1.1);
        }
        .swiper-pagination-bullet {
          background: #fff;
          opacity: 0.5;
        }
        .swiper-pagination-bullet-active {
          opacity: 1;
          background: var(--primary-color);
          width: 30px;
          border-radius: 5px;
        }
        @media (max-width: 992px) {
          .wpo-hero-slider .swiper-button-prev::after,
          .wpo-hero-slider .swiper-button-next::after {
            font-size: 40px !important;
          }
        }
        @media (max-width: 576px) {
          .wpo-hero-slider .swiper-button-next, 
          .wpo-hero-slider .swiper-button-prev {
            display: none;
          }
        }
        .hero-container {
          padding-left: 80px;
          padding-right: 80px;
        }
        @media (max-width: 991px) {
          .slide-inner {
            align-items: flex-start !important;
            padding-top: 150px;
          }
          .hero-container {
            padding-left: 15px;
            padding-right: 15px;
          }
        }
        @media (min-width: 992px) {
          .slide-inner {
            align-items: center !important;
            padding-top: 200px;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;
